import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Grid,
  Divider,
  Alert,
  Paper,
  Button
} from '@mui/material';
import {
  Business,
  LocationOn,
  EventSeat,
  ArrowBack,
  Refresh,
  QrCode2,
  Phone,
  Email,
  CheckCircle,
  Block,
  Download,
  Share
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import api from '../../utils/api';
import { GRADIENTS, RADIUS } from '../../theme/tokens';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

/**
 * Library Details - View library information and QR code
 */
function LibraryDetails({ showSnackbar }) {
  const navigate = useNavigate();

  const [library, setLibrary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  useEffect(() => {
    fetchLibraryData();
  }, []);

  const fetchLibraryData = async () => {
    try {
      setLoading(true);

      // Fetch librarian's assigned library
      const libraryResponse = await api.get('/librarian/library');

      if (!libraryResponse.data.data.library) {
        showSnackbar('No library assigned', 'error');
        setLoading(false);
        return;
      }

      const myLibrary = libraryResponse.data.data.library;
      setLibrary(myLibrary);

      // Generate QR code URL
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const baseUrl = apiUrl.replace('/api', '');
      const qrUrl = `${baseUrl}/api/qr/library/${myLibrary.id}`;
      setQrCodeUrl(qrUrl);
    } catch (err) {
      console.error('Error fetching library:', err);
      showSnackbar('Failed to load library details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQR = () => {
    if (!qrCodeUrl) return;

    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `${library?.name || 'library'}-qr-code.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSnackbar('QR code downloaded!', 'success');
  };

  const handleShareQR = async () => {
    if (!qrCodeUrl || !library) return;

    try {
      if (navigator.share) {
        await navigator.share({
          title: `${library.name} - QR Code`,
          text: `Scan this QR code to check in/out at ${library.name}`,
          url: qrCodeUrl
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(qrCodeUrl);
        showSnackbar('QR code URL copied to clipboard!', 'info');
      }
    } catch (err) {
      console.error('Error sharing:', err);
      showSnackbar('Failed to share QR code', 'error');
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>Loading library details...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' }}>
      <Container maxWidth="sm" sx={{ py: 2, pb: 10 }}>
        <MotionBox
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Header */}
          <MotionBox variants={itemVariants} mb={3}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <IconButton
                onClick={() => navigate('/librarian/manage')}
                sx={{
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' }
                }}
              >
                <ArrowBack />
              </IconButton>
              <Box flex={1}>
                <Typography variant="h4" fontWeight="800" gutterBottom>
                  Library Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your library information & QR code
                </Typography>
              </Box>
              <IconButton
                onClick={fetchLibraryData}
                sx={{
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' }
                }}
              >
                <Refresh />
              </IconButton>
            </Box>
          </MotionBox>

          {/* Library Card */}
          <MotionCard
            variants={itemVariants}
            sx={{
              borderRadius: RADIUS.xl,
              mb: 2,
              background: GRADIENTS.brand,
              color: 'white',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {/* Background Pattern */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.1,
                backgroundImage: `radial-gradient(circle at 20px 20px, white 2px, transparent 0)`,
                backgroundSize: '40px 40px'
              }}
            />

            <CardContent sx={{ p: 3, position: 'relative' }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <Business sx={{ fontSize: 30 }} />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h5" fontWeight="700" gutterBottom>
                    {library?.name || 'Your Library'}
                  </Typography>
                  <Chip
                    icon={library?.active ? <CheckCircle /> : <Block />}
                    label={library?.active ? 'Active' : 'Inactive'}
                    size="small"
                    sx={{
                      bgcolor: library?.active ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                </Box>
              </Box>

              <Box display="flex" alignItems="start" gap={1}>
                <LocationOn fontSize="small" sx={{ mt: 0.5, opacity: 0.9 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {library?.address || 'Address not set'}
                </Typography>
              </Box>
            </CardContent>
          </MotionCard>

          {/* Library Stats */}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={6}>
              <MotionCard
                variants={itemVariants}
                sx={{ borderRadius: RADIUS.lg, textAlign: 'center' }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Avatar
                    sx={{
                      width: 50,
                      height: 50,
                      bgcolor: 'primary.light',
                      color: 'primary.main',
                      mx: 'auto',
                      mb: 1
                    }}
                  >
                    <EventSeat />
                  </Avatar>
                  <Typography variant="h4" fontWeight="800" color="primary">
                    {library?.capacity || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Seats
                  </Typography>
                </CardContent>
              </MotionCard>
            </Grid>

            <Grid item xs={6}>
              <MotionCard
                variants={itemVariants}
                sx={{ borderRadius: RADIUS.lg, textAlign: 'center' }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Avatar
                    sx={{
                      width: 50,
                      height: 50,
                      bgcolor: 'success.light',
                      color: 'success.main',
                      mx: 'auto',
                      mb: 1
                    }}
                  >
                    <QrCode2 />
                  </Avatar>
                  <Typography variant="h4" fontWeight="800" color="success.main">
                    {library?.plans?.length || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Plans Available
                  </Typography>
                </CardContent>
              </MotionCard>
            </Grid>
          </Grid>

          {/* Amenities */}
          {library?.amenities && library.amenities.length > 0 && (
            <MotionCard variants={itemVariants} sx={{ borderRadius: RADIUS.xl, mb: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="700" gutterBottom>
                  Amenities
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {library.amenities.map((amenity, index) => (
                    <Chip
                      key={index}
                      label={amenity}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  ))}
                </Box>
              </CardContent>
            </MotionCard>
          )}

          {/* QR Code Section */}
          <MotionCard variants={itemVariants} sx={{ borderRadius: RADIUS.xl }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar
                  sx={{
                    width: 50,
                    height: 50,
                    background: GRADIENTS.brand
                  }}
                >
                  <QrCode2 />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="700">
                    Library QR Code
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Students scan this to check in/out
                  </Typography>
                </Box>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                Display this QR code at your library entrance. Students can scan it to check in and check out.
              </Alert>

              {/* QR Code Display */}
              <Box
                sx={{
                  bgcolor: 'white',
                  p: 3,
                  borderRadius: RADIUS.lg,
                  textAlign: 'center',
                  border: '2px dashed',
                  borderColor: 'divider',
                  mb: 2
                }}
              >
                {qrCodeUrl ? (
                  <Box>
                    <img
                      src={qrCodeUrl}
                      alt="Library QR Code"
                      style={{
                        width: '100%',
                        maxWidth: 300,
                        height: 'auto',
                        margin: '0 auto',
                        display: 'block'
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" mt={2} display="block">
                      Library ID: {library?.id?.substring(0, 8)}
                    </Typography>
                  </Box>
                ) : (
                  <Box py={6}>
                    <QrCode2 sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      QR code could not be loaded
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Action Buttons */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleDownloadQR}
                    disabled={!qrCodeUrl}
                    sx={{
                      borderRadius: RADIUS.md,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Download QR
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Share />}
                    onClick={handleShareQR}
                    disabled={!qrCodeUrl}
                    sx={{
                      borderRadius: RADIUS.md,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      background: GRADIENTS.brand
                    }}
                  >
                    Share QR
                  </Button>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Instructions */}
              <Box>
                <Typography variant="subtitle2" fontWeight="700" gutterBottom>
                  How to use:
                </Typography>
                <Box component="ol" sx={{ pl: 2, m: 0 }}>
                  <Typography component="li" variant="body2" color="text.secondary" mb={1}>
                    Print this QR code or display it on a screen at your library entrance
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary" mb={1}>
                    Students with active bookings can scan this QR code to check in
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary" mb={1}>
                    Students scan the same QR code again to check out when leaving
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    You can also use the Scanner tab to manually scan student QR passes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </MotionCard>
        </MotionBox>
      </Container>
    </Box>
  );
}

export default LibraryDetails;
